import type { ModalDialogProps } from '@atlaskit/modal-dialog';

/**
 * Marker attribute placed on the persistent iframe host (see
 * AiPlannerIframeProvider). The host is portaled to `document.body` and overlaid
 * on top of the modal, so it lives OUTSIDE the modal's DOM subtree. The modal's
 * focus lock would otherwise refuse to let keyboard focus reach it, so we
 * allowlist the host (and its descendants) out of the focus lock below.
 */
export const AI_PLANNER_IFRAME_HOST_ATTRIBUTE = 'data-ai-planner-iframe-host';

/**
 * Props forwarded to the Planner iframe modal's underlying
 * `@atlaskit/modal-dialog` via `ModalTrigger`'s `modalProps`.
 *
 * The planner iframe is rendered by `AiPlannerIframeProvider` into a host that
 * is portaled to `document.body` (so it can sit above the modal's stacking
 * context). Because that host is not a descendant of the modal, the modal's
 * `react-focus-lock` traps focus away from it and the embedded planner cannot be
 * focused. `focusLockAllowlist` excludes the host subtree from the focus lock so
 * keyboard focus can move into the iframe while every other element stays
 * trapped inside the modal as usual.
 */
export const modalProps: ModalDialogProps = {
	width: '95vw',
	height: '95vh',
	focusLockAllowlist: (element: HTMLElement) =>
		// Return `false` for the iframe host (and anything inside it) so the focus
		// lock ignores it — making the embedded planner reachable. Return `true`
		// for everything else so the modal keeps trapping focus normally.
		element.closest(`[${AI_PLANNER_IFRAME_HOST_ATTRIBUTE}]`) == null,
};
